/* Scoped event gallery. No analytics, global libraries, or pasted scripts are added. */
(() => {
  if (customElements.get('gb-events')) return;
  class GoldBarEvents extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      const on = (target, type, fn) => target.addEventListener(type, fn, { signal: this.abort.signal });
      this.id = `gb-events-top-${this.dataset.sectionId}`;
      this.grid = this.querySelector('[data-event-grid]');
      this.cards = [...this.querySelectorAll('[data-event-card]')].sort((a, b) => Number(a.dataset.order || 0) - Number(b.dataset.order || 0));
      this.cards.forEach(card => this.grid.append(card));
      this.panels = new Map([...this.querySelectorAll('[data-event-panel]')].map(panel => [panel.dataset.eventPanel, panel]));
      this.panelHome = this.querySelector('[data-event-panels]');
      this.dialog = this.querySelector('[data-event-dialog]');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.content = this.querySelector('[data-dialog-content]');
      this.panels.forEach(panel => { panel.hidden = true; });
      this.classList.add('gb-events--enhanced');
      this.querySelectorAll('[data-event-open]').forEach(link => {
        link.setAttribute('aria-haspopup', 'dialog');
        on(link, 'click', event => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          this.opener = link;
          this.open(link.dataset.eventOpen, true);
        });
      });
      this.querySelectorAll('[data-event-close]').forEach(button => on(button, 'click', () => this.dialog.close()));
      on(this.dialog, 'close', () => this.cleanup());
      on(this.dialog, 'click', event => { if (event.target === this.dialog) { const r = this.dialog.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.dialog.close(); } });
      on(this.querySelector('[data-event-prev]'), 'click', () => this.move(-1));
      on(this.querySelector('[data-event-next]'), 'click', () => this.move(1));
      this.querySelector('[data-event-prev]').hidden = this.cards.length < 2;
      this.querySelector('[data-event-next]').hidden = this.cards.length < 2;
      on(window, 'hashchange', () => this.fromHash());
      if (this.dataset.editor) {
        on(document, 'shopify:block:select', event => {
          if (this.panels.has(event.detail.blockId)) this.open(event.detail.blockId, false);
        });
        on(document, 'shopify:section:unload', event => { if (String(event.detail.sectionId) === this.dataset.sectionId) this.disconnectedCallback(); });
      }
      this.fromHash();
    }
    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.cleanup();
      this.abort?.abort();
      this.initialized = false;
    }
    fromHash() {
      const key = window.location.hash.slice(1).replace(/^event-/, '');
      if (window.location.hash.startsWith('#event-') && this.panels.has(key)) this.open(key, false);
      else if (this.dialog.open) this.dialog.close();
    }
    move(delta) {
      const i = this.cards.findIndex(card => card.dataset.eventCard === this.active);
      this.open(this.cards[(i + delta + this.cards.length) % this.cards.length].dataset.eventCard, true);
      this.dialog.querySelector('[data-event-close]').focus({ preventScroll: true });
    }
    open(key, updateHash) {
      const panel = this.panels.get(key);
      if (!panel) return;
      if (this.active && this.active !== key) this.returnPanel();
      this.active = key;
      panel.hidden = false;
      this.content.append(panel);
      this.dialog.setAttribute('aria-labelledby', panel.querySelector('h2').id);
      if (!this.dialog.open) {
        this.previousHash = window.location.hash.startsWith('#event-') ? '' : window.location.hash;
        this.previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        this.dialog.showModal();
      }
      this.dialog.scrollTop = 0;
      this.setupTickets(panel);
      if (updateHash) this.setHash(`#event-${key}`);
    }
    setHash(hash) {
      // Preserve Shopify's preview_theme_id and every other URL parameter.
      try { const url = new URL(window.location.href); url.hash = hash; window.history.replaceState(window.history.state, '', url); } catch (_) { /* Local-file previews can restrict history. */ }
    }
    returnPanel() {
      const panel = this.panels.get(this.active);
      if (!panel) return;
      panel.querySelector('[data-embed-mount]')?.replaceChildren();
      panel.hidden = true;
      this.panelHome.append(panel);
    }
    cleanup() {
      if (!this.active) return;
      this.returnPanel();
      this.active = null;
      if (this.previousOverflow !== undefined) document.documentElement.style.overflow = this.previousOverflow;
      if (window.location.hash.startsWith('#event-')) this.setHash(this.previousHash || '');
      this.opener?.focus({ preventScroll: true });
    }
    embedUrl(panel) {
      const content = panel.querySelector('[data-embed-input]')?.content;
      const input = content ? [...content.childNodes].map(node => node.textContent || '').join('').trim() : '';
      if (!input) return null;
      let raw = input;
      if (input.startsWith('<')) {
        const doc = new DOMParser().parseFromString(input, 'text/html');
        const frames = doc.querySelectorAll('iframe');
        // Only an iframe's URL is extracted. No supplied HTML, handlers or scripts execute.
        if (frames.length !== 1 || doc.querySelector('script')) return null;
        raw = frames[0].getAttribute('src') || '';
      }
      try {
        const url = new URL(raw);
        if (url.protocol !== 'https:' || url.username || url.password || url.port) return null;
        if (url.hostname !== 'ticketfairy.com' && !url.hostname.endsWith('.ticketfairy.com')) return null;
        // A normal hosted event link is not an embed. Never invent a checkout URL from an ID.
        if (!/embed|widget|checkout/i.test(url.pathname + url.search) && !input.startsWith('<')) return null;
        return url;
      } catch (_) { return null; }
    }
    setupTickets(panel) {
      const area = panel.querySelector('[data-ticket-area]');
      if (!area || area.dataset.soldOut === 'true' || area.dataset.method !== 'embed') return;
      const link = area.querySelector('[data-ticket-link]');
      const notice = area.querySelector('[data-embed-notice]');
      const mount = area.querySelector('[data-embed-mount]');
      const fallback = area.querySelector('[data-embed-fallback]');
      const url = this.embedUrl(panel);
      const fingerprint = u => { const copy = new URL(u.href); copy.hash = ''; ['utm_source','utm_medium','utm_campaign','utm_content','utm_term'].forEach(k => copy.searchParams.delete(k)); copy.searchParams.sort(); return copy.href; };
      const duplicate = url && [...this.panels.values()].some(other => {
        if (other === panel || other.querySelector('[data-ticket-area]')?.dataset.method !== 'embed') return false;
        const otherUrl = this.embedUrl(other);
        return otherUrl && fingerprint(otherUrl) === fingerprint(url);
      });
      const useExternal = reason => {
        mount.replaceChildren();
        if (link) link.hidden = false;
        fallback.hidden = true;
        notice.hidden = !this.dataset.editor;
        notice.textContent = reason;
      };
      if (!url || duplicate) {
        useExternal(duplicate ? 'Duplicate checkout URL: use a unique event-specific embed. Showing the external ticket button.' : 'A supported Ticket Fairy iframe code or embed URL is needed. Showing the external ticket button when one is configured.');
        return;
      }
      if (link) link.hidden = true;
      notice.hidden = true;
      fallback.hidden = !link;
      fallback.onclick = () => useExternal('External ticket page selected.');
      mount.replaceChildren();
      const iframe = document.createElement('iframe');
      iframe.title = `Ticket checkout — ${panel.querySelector('h2').textContent}`;
      iframe.src = url.href;
      iframe.allow = 'payment';
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      iframe.style.height = `${Math.max(400, Math.min(1400, Number(area.dataset.embedHeight) || 800))}px`;
      iframe.addEventListener('error', () => useExternal('Checkout could not load. Showing the external ticket button.'), { once: true });
      mount.append(iframe);
    }
  }
  customElements.define('gb-events', GoldBarEvents);
})();
